//@ts-nocheck
import { useState, useEffect } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { useToast } from '@/components/ui/use-toast'
import { Upload, RefreshCw, AlertTriangle, ThumbsUp, ThumbsDown, Star, ClipboardList, ImagePlus, X, Play, Info, Menu, HelpCircle, Shield, FileText, LogOut } from 'lucide-react'
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar'
import { Separator } from '@/components/ui/separator'
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet'
import { processImagesDirectly, resizeImage, updateConsultationFeedback, ProcessImagesResult } from '@/lib/supabase'
import { Textarea } from '@/components/ui/textarea'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Dialog, DialogContent } from "@/components/ui/dialog"
import { cn } from '@/lib/utils'
import ImageCropModal from '@/components/ImageCropModal'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"

export default function Dashboard(): JSX.Element {
  const { user, signOut } = useAuth()
  const { toast } = useToast()
  const [context, setContext] = useState<string>('')
  const [previewUrls, setPreviewUrls] = useState<string[]>([])
  const [files, setFiles] = useState<File[]>([])
  const [diagnosis, setDiagnosis] = useState<string>('')
  const [isLoading, setIsLoading] = useState(false)
  const [lastFeedback, setLastFeedback] = useState<'positive' | 'negative' | null>(null)
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [currentConsultationId, setCurrentConsultationId] = useState<string | null>(null)
  const [showCropModal, setShowCropModal] = useState(false)
  const [tempImageUrl, setTempImageUrl] = useState<string | null>(null)
  const [showFooter, setShowFooter] = useState(true)
  const [showMobileAd, setShowMobileAd] = useState(true)
  const [invalidImageIndexes, setInvalidImageIndexes] = useState<number[]>([])
  const [hasInvalidImageError, setHasInvalidImageError] = useState(false)
  const [showLoadingModal, setShowLoadingModal] = useState(false)
  const [isDragging, setIsDragging] = useState(false)

  // Função para lidar com o logout
  const handleSignOut = async () => {
    try {
      await signOut()
      toast({
        title: "Logout realizado com sucesso",
        description: "Você foi desconectado da sua conta.",
      })
    } catch (error) {
      console.error('Erro ao fazer logout:', error)
      toast({
        variant: 'destructive',
        title: 'Erro ao fazer logout',
        description: 'Ocorreu um erro ao tentar desconectar. Tente novamente.',
      })
    }
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowFooter(false)
    }, 15000)

    return () => clearTimeout(timer)
  }, [])

  const handleFileSelect = async (selectedFiles: FileList) => {
    try {
      // Processar múltiplos arquivos
      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i]
        
        if (!file) {
          continue // Pular arquivos vazios
        }

        if (!file.type.startsWith('image/')) {
          toast({
            variant: 'destructive',
            title: 'Tipo de arquivo não suportado',
            description: 'Por favor, selecione apenas imagens.',
          })
          continue // Pular arquivos que não são imagens
        }

        const MAX_SIZE = 10 * 1024 * 1024
        if (file.size > MAX_SIZE) {
          toast({
            variant: 'destructive',
            title: 'Arquivo muito grande',
            description: 'A imagem deve ter no máximo 10MB.',
          })
          continue // Pular arquivos muito grandes
        }

        // Verificar se ainda podemos adicionar mais imagens
        if (files.length >= 3) {
          toast({
            variant: 'destructive',
            title: 'Limite de imagens atingido',
            description: 'Você pode enviar no máximo 3 imagens por consulta.',
          })
          break // Parar de processar se já atingimos o limite
        }

        // Adicionar imagem
        const url = URL.createObjectURL(file)
        setPreviewUrls(prev => [...prev, url])
        setFiles(prev => [...prev, file])
        
        // Resetar estados de erro
        setHasInvalidImageError(false)
        setInvalidImageIndexes([])
        
        toast({
          title: 'Imagem adicionada',
          description: `${files.length + 1}/3 imagens`,
        })
      }
    } catch (error) {
      console.error('Erro ao selecionar arquivo:', error)
      toast({
        variant: 'destructive',
        title: 'Erro ao adicionar imagem',
        description: error instanceof Error ? error.message : 'Ocorreu um erro ao adicionar a imagem.',
      })
    }
  }

  // Funções para lidar com drag and drop
  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    if (!isDragging) {
      setIsDragging(true)
    }
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
    
    if (isLoading || files.length >= 3) return
    
    const droppedFiles = e.dataTransfer.files
    if (droppedFiles && droppedFiles.length > 0) {
      handleFileSelect(droppedFiles)
    }
  }

  const handleCroppedImage = async (croppedBlob: Blob) => {
    try {
      // Criar um arquivo a partir do blob
      const croppedFile = new File([croppedBlob], 'cropped-image.jpg', {
        type: 'image/jpeg',
      })

      // Adicionar a imagem recortada
      const url = URL.createObjectURL(croppedBlob)
      setPreviewUrls(prev => [...prev, url])
      setFiles(prev => [...prev, croppedFile])

      // Limpar a imagem temporária
      if (tempImageUrl) {
        URL.revokeObjectURL(tempImageUrl)
        setTempImageUrl(null)
      }

      toast({
        title: 'Imagem adicionada',
        description: `${previewUrls.length + 1}/3 imagens`,
      })
    } catch (error) {
      console.error('Erro ao processar imagem recortada:', error)
      toast({
        variant: 'destructive',
        title: 'Erro ao processar imagem',
        description: 'Ocorreu um erro ao processar a imagem recortada.',
      })
    }
  }

  const removeImage = (index: number) => {
    setPreviewUrls(prev => prev.filter((_, i) => i !== index))
    setFiles(prev => prev.filter((_, i) => i !== index))

    // Atualizar os índices inválidos após remover uma imagem
    setInvalidImageIndexes(prev => {
      const newIndexes = prev
        .filter(i => i !== index) // Remove o índice excluído
        .map(i => i > index ? i - 1 : i) // Ajusta os índices maiores que o excluído
      
      // Se não houver mais índices inválidos, resetar o estado de erro
      if (newIndexes.length === 0) {
        setHasInvalidImageError(false)
      }
      
      return newIndexes
    })
  }

  const processImages = async () => {
    if (files.length === 0) {
      toast({
        variant: 'destructive',
        title: 'Nenhuma imagem selecionada',
        description: 'Por favor, selecione pelo menos uma imagem para análise.',
      })
      return
    }

    if (context.trim().length < 10 && !showConfirmDialog) {
      setShowConfirmDialog(true)
      return
    }

    setShowConfirmDialog(false)
    setIsLoading(true)
    setShowLoadingModal(true)
    setDiagnosis('')
    setCurrentConsultationId(null)
    setInvalidImageIndexes([])
    setHasInvalidImageError(false)

    try {
      if (!user) throw new Error('Usuário não autenticado')

      // Processar imagens diretamente sem redimensionamento
      const result: ProcessImagesResult = await processImagesDirectly(files, context, user.id)
      
      // Verificar se a resposta contém a mensagem de erro específica
      const errorMessage = "Essa imagem não parece ser um eletrocardiograma. Se ela é, tente enviar novamente com mais qualidade ou sem outros elementos na foto."
      
      if (result.images[0]?.diagnosis?.includes(errorMessage)) {
        setHasInvalidImageError(true)
        // Encontrar quais imagens são inválidas
        const invalidIndexes = result.images
          .map((img, index) => img.diagnosis?.includes(errorMessage) ? index : -1)
          .filter(index => index !== -1)
        setInvalidImageIndexes(invalidIndexes)
        
        toast({
          variant: 'destructive',
          title: 'Imagens inválidas detectadas',
          description: 'Algumas imagens não parecem ser eletrocardiogramas ou precisam de melhor qualidade. Por favor, revise as imagens marcadas em vermelho.',
        })
      } else if (result.images[0]?.diagnosis) {
        setDiagnosis(result.images[0].diagnosis)
        setCurrentConsultationId(result.consultationId)
        
        toast({
          title: 'Análise concluída!',
          description: 'Sua opinião é muito importante para melhorarmos nosso serviço.',
        })
      }
    } catch (error) {
      console.error('Erro completo:', error)
      toast({
        variant: 'destructive',
        title: 'Erro ao processar imagens',
        description: error instanceof Error 
          ? `Erro: ${error.message}${error.cause ? ` (Causa: ${error.cause})` : ''}`
          : 'Ocorreu um erro ao processar suas imagens.',
      })
    } finally {
      setIsLoading(false)
      setShowLoadingModal(false)
    }
  }

  const handleQuickFeedback = async (type: 'positive' | 'negative') => {
    try {
      if (!currentConsultationId) {
        throw new Error('ID da consulta não encontrado')
      }

      // Atualizar o feedback no banco de dados
      await updateConsultationFeedback(
        currentConsultationId,
        type === 'positive' ? 'Análise útil' : 'Análise não útil'
      )

      setLastFeedback(type)
      
      toast({
        title: type === 'positive' ? 'Obrigado pelo feedback positivo!' : 'Obrigado pelo feedback!',
        description: type === 'positive' 
          ? 'Que bom que pudemos ajudar! Conheça mais sobre nossas soluções.' 
          : 'Agradecemos sua avaliação. Estamos sempre buscando melhorar.',
      })
    } catch (error) {
      console.error('Erro ao salvar feedback:', error)
      toast({
        variant: 'destructive',
        title: 'Erro ao salvar feedback',
        description: 'Não foi possível salvar sua avaliação. Tente novamente.',
      })
    }
  }

  const handleNewAnalysis = () => {
    setPreviewUrls([])
    setFiles([])
    setContext('')
    setDiagnosis('')
    setLastFeedback(null)
    setCurrentConsultationId(null)
  }

  return (
    <div className="flex flex-col min-h-screen">
      <main className="flex-1 container mx-auto px-4 py-6 mb-[120px] sm:mb-[100px]">
        <div className="mx-auto max-w-2xl space-y-8">
          {/* Propaganda Mobile */}
          {showMobileAd && (
            <div className="sm:hidden relative flex items-center bg-[#6AB99C]/10 px-4 py-3 rounded-xl group hover:bg-[#6AB99C]/20 transition-all duration-300">
              <div className="flex-1">
                <div className="text-xs text-[#6AB99C] font-medium">Simples.Health</div>
                <div className="text-sm font-semibold">Ganhe 2h no seu dia</div>
                <div className="text-xs text-muted-foreground mt-1">
                  Conheça nossa plataforma completa de documentação médica com IA
                </div>
              </div>
              <a 
                href="https://simples.health" 
                target="_blank" 
                rel="noreferrer" 
                className="flex items-center gap-1 text-sm font-medium text-white bg-[#6AB99C] h-10 px-4 rounded-lg group-hover:scale-105 transition-all duration-300 whitespace-nowrap"
              >
                Testar Grátis
                <svg 
                  className="w-4 h-4 transform group-hover:translate-x-0.5 transition-transform" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" 
                  />
                </svg>
              </a>
              <button 
                onClick={() => setShowMobileAd(false)}
                className="absolute top-2 right-2 p-1 rounded-full hover:bg-white/20 transition-colors"
                aria-label="Fechar propaganda"
              >
                <X className="h-4 w-4 text-muted-foreground" />
              </button>
            </div>
          )}

          {!diagnosis ? (
            <div className="space-y-8">
              {/* Contexto Clínico com Ícone e Tooltip */}
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <ClipboardList className="w-6 h-6 text-primary" />
                  </div>
                  <h2 className="text-2xl font-semibold">Contexto Clínico</h2>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button 
                          className="p-1.5 rounded-full hover:bg-primary/10 transition-colors"
                          onClick={(e) => {
                            // Previne o comportamento padrão no mobile
                            e.preventDefault()
                            e.stopPropagation()
                          }}
                        >
                          <Info className="w-5 h-5 text-primary" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent 
                        side="top" 
                        align="start" 
                        className="text-foreground w-[calc(100vw-2rem)] max-w-[calc(100vw-2rem)] sm:w-auto sm:max-w-none"
                      >
                        <div className="space-y-3">
                          <div className="flex items-center gap-2">
                            <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                            <p className="font-medium">Como obter melhores resultados:</p>
                          </div>
                          <ul className="space-y-2 list-none">
                            <li className="flex items-start gap-2">
                              <span className="text-primary">•</span>
                              <span>Descreva os sintomas principais e sua duração</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="text-primary">•</span>
                              <span>Inclua histórico médico relevante e alergias</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="text-primary">•</span>
                              <span>Mencione condições pré-existentes importantes</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="text-primary">•</span>
                              <span>Informe medicamentos em uso atual</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="text-primary">•</span>
                              <span>Adicione resultados de exames anteriores relevantes</span>
                            </li>
                          </ul>
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <Textarea
                  placeholder="Descreva o contexto do paciente, sintomas, histórico médico relevante, etc. Quanto mais detalhado, melhor a análise..."
                  value={context}
                  onChange={(e) => setContext(e.target.value)}
                  className="min-h-[160px] text-lg p-4 border-primary/20 focus:border-primary transition-colors"
                />
              </div>

              {/* Upload de Imagens com Ícone e Tooltip */}
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <ImagePlus className="w-6 h-6 text-primary" />
                  </div>
                  <h2 className="text-2xl font-semibold">Imagens</h2>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button 
                          className="p-1.5 rounded-full hover:bg-primary/10 transition-colors"
                          onClick={(e) => {
                            // Previne o comportamento padrão no mobile
                            e.preventDefault()
                            e.stopPropagation()
                          }}
                        >
                          <Info className="w-5 h-5 text-primary" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent 
                        side="top" 
                        align="start" 
                        className="text-foreground w-[calc(100vw-2rem)] max-w-[calc(100vw-2rem)] sm:w-auto sm:max-w-none"
                      >
                        <div className="space-y-3">
                          <div className="flex items-center gap-2">
                            <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                            <p className="font-medium">Dicas para melhor análise:</p>
                          </div>
                          <ul className="space-y-2 list-none">
                            <li className="flex items-start gap-2">
                              <span className="text-primary">•</span>
                              <span>Envie apenas eletrocardiogramas nítidos e bem iluminados</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="text-primary">•</span>
                              <span>Evite incluir elementos externos como tela de computador ou monitores</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="text-primary">•</span>
                              <span>Certifique-se que a imagem está na orientação correta</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="text-primary">•</span>
                              <span>Você pode enviar até 3 imagens por análise</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="text-primary">•</span>
                              <span>Imagens muito escuras ou com baixa qualidade podem prejudicar a análise</span>
                            </li>
                          </ul>
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>

                <div className="flex flex-col gap-4">
                  <div className="grid grid-cols-1 sm:grid-cols-1 gap-2">
                    <Button
                      size="lg"
                      className="w-full h-14 text-lg bg-primary hover:bg-primary/90 transition-all duration-300 flex items-center justify-center gap-2"
                      onClick={() => {
                        const input = document.createElement('input')
                        input.type = 'file'
                        input.accept = 'image/*'
                        input.multiple = true
                        input.onchange = (e) => {
                          const files = (e.target as HTMLInputElement).files
                          if (files) handleFileSelect(files)
                        }
                        input.click()
                      }}
                      disabled={isLoading || previewUrls.length >= 3}
                    >
                      <Upload className="h-5 w-5 sm:h-6 sm:w-6" />
                      <span className="text-base sm:text-lg">
                        <span className="hidden sm:inline">Adicionar Imagens</span>
                        <span className="sm:hidden">Galeria</span>
                      </span>
                      {previewUrls.length > 0 && (
                        <span className="text-xs sm:text-sm bg-white/20 px-2 py-1 rounded-full ml-2">
                          {previewUrls.length}/3
                        </span>
                      )}
                    </Button>

                    <Button
                      size="lg"
                      className="sm:hidden w-full h-14 text-lg bg-primary hover:bg-primary/90 transition-all duration-300 flex items-center justify-center gap-2"
                      onClick={() => {
                        const input = document.createElement('input')
                        input.type = 'file'
                        input.accept = 'image/*'
                        input.capture = 'environment'
                        input.onchange = (e) => {
                          const files = (e.target as HTMLInputElement).files
                          if (files) {
                            // Temporariamente desabilitado o editor de imagem
                            // const file = files[0]
                            // const url = URL.createObjectURL(file)
                            // setTempImageUrl(url)
                            // setShowCropModal(true)
                            handleFileSelect(files)
                          }
                        }
                        input.click()
                      }}
                      disabled={isLoading || previewUrls.length >= 3}
                    >
                      <svg 
                        className="h-5 w-5" 
                        fill="none" 
                        viewBox="0 0 24 24" 
                        stroke="currentColor"
                      >
                        <path 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                          strokeWidth={2} 
                          d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                        />
                        <path 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                          strokeWidth={2} 
                          d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                      <span className="text-base">Câmera</span>
                    </Button>
                  </div>

                  {/* Área de Drag and Drop - Apenas Desktop */}
                  <div 
                    className={cn(
                      "hidden sm:flex flex-col items-center justify-center border-2 border-dashed rounded-xl p-8 transition-all duration-300 cursor-pointer",
                      isDragging 
                        ? "border-primary bg-primary/5" 
                        : "border-primary/20 hover:border-primary/40 hover:bg-primary/5",
                      (isLoading || previewUrls.length >= 3) && "opacity-50 cursor-not-allowed"
                    )}
                    onDragEnter={handleDragEnter}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => {
                      if (isLoading || previewUrls.length >= 3) return
                      
                      const input = document.createElement('input')
                      input.type = 'file'
                      input.accept = 'image/*'
                      input.multiple = true
                      input.onchange = (e) => {
                        const files = (e.target as HTMLInputElement).files
                        if (files) handleFileSelect(files)
                      }
                      input.click()
                    }}
                  >
                    <div className="flex flex-col items-center gap-4 py-6">
                      <div className={cn(
                        "w-16 h-16 rounded-full flex items-center justify-center transition-all duration-300",
                        isDragging ? "bg-primary/20" : "bg-primary/10"
                      )}>
                        <Upload className="h-8 w-8 text-primary" />
                      </div>
                      <div className="text-center space-y-2">
                        <p className="text-lg font-medium text-foreground">
                          {isDragging ? "Solte para adicionar" : "Arraste e solte suas imagens aqui"}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          ou clique para selecionar do seu computador
                        </p>
                        <p className="text-xs text-muted-foreground mt-2">
                          Suporta imagens JPG, PNG e JPEG até 10MB
                        </p>
                      </div>
                    </div>
                  </div>

                  {previewUrls.length > 0 && (
                    <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                      {previewUrls.map((url, index) => (
                        <div 
                          key={index} 
                          className={cn(
                            "relative aspect-square rounded-lg border group overflow-hidden bg-card",
                            invalidImageIndexes.includes(index) && "border-red-500 border-2",
                            hasInvalidImageError && !invalidImageIndexes.includes(index) && "opacity-50"
                          )}
                        >
                          <img
                            src={url}
                            alt={`Preview ${index + 1}`}
                            className="absolute inset-0 h-full w-full rounded-lg object-cover transition-transform duration-300 group-hover:scale-105"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/0 to-black/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                          
                          {invalidImageIndexes.includes(index) && (
                            <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                              <div className="bg-white/90 p-3 rounded-lg text-center max-w-[80%]">
                                <AlertTriangle className="w-6 h-6 text-red-500 mx-auto mb-2" />
                                <p className="text-sm font-medium text-red-700">
                                  Esta imagem não parece ser um eletrocardiograma válido
                                </p>
                                <p className="text-xs text-gray-600 mt-1">
                                  Tente enviar uma imagem com melhor qualidade ou sem outros elementos
                                </p>
                              </div>
                            </div>
                          )}
                          
                          <button
                            onClick={() => removeImage(index)}
                            className="absolute top-2 right-2 p-1.5 rounded-full bg-white/90 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 hover:bg-white transition-all duration-300 shadow-lg"
                            aria-label="Remover imagem"
                          >
                            <X className="w-4 h-4 text-gray-700" />
                          </button>
                          <div className="absolute bottom-2 left-2 px-2 py-1 rounded-md bg-white/90 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-300 text-xs font-medium text-gray-700">
                            Imagem {index + 1}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {hasInvalidImageError && (
                    <div className="mt-4 p-4 rounded-lg bg-red-50 border border-red-200">
                      <div className="flex items-start gap-3">
                        <div className="mt-1">
                          <AlertTriangle className="w-5 h-5 text-red-500" />
                        </div>
                        <div>
                          <h4 className="text-sm font-semibold text-red-800">Algumas imagens precisam ser revisadas</h4>
                          <p className="text-sm text-red-600 mt-1">
                            As imagens marcadas em vermelho não puderam ser processadas. Você pode:
                          </p>
                          <ul className="mt-2 text-sm text-red-600 list-disc list-inside space-y-1">
                            <li>Remover as imagens inválidas clicando no X</li>
                            <li>Adicionar novas imagens com melhor qualidade</li>
                            <li>Recortar as imagens para conter apenas o eletrocardiograma</li>
                            <li>Garantir que as imagens são eletrocardiogramas sem outros elementos, como a tela de um computador</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}

                  {previewUrls.length > 0 && (
                    <Button 
                      size="lg"
                      onClick={processImages}
                      className={cn(
                        "w-full h-14 text-lg transition-all duration-300 flex items-center justify-center gap-2",
                        hasInvalidImageError 
                          ? "bg-red-500 hover:bg-red-600" 
                          : "bg-primary hover:bg-primary/90"
                      )}
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <div className="flex flex-col items-center gap-3">
                          <div className="flex items-center gap-3">
                            <div className="relative">
                              <RefreshCw className="h-5 w-5 sm:h-6 sm:w-6 animate-spin" />
                              <div className="absolute -top-1 -right-1 w-2 h-2 bg-white rounded-full animate-ping" />
                            </div>
                            <span className="text-base sm:text-lg">Analisando imagens...</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-white/80 animate-pulse">
                            <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                            <span>Não esqueça de avaliar a análise ao final para melhorarmos cada vez mais!</span>
                          </div>
                        </div>
                      ) : (
                        <>
                          <Play className="h-5 w-5 sm:h-6 sm:w-6" />
                          <span className="text-base sm:text-lg">
                            {hasInvalidImageError ? 'Tentar novamente' : 'Analisar com IA'}
                          </span>
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-8">
              {/* Imagens com Zoom */}
              <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                {previewUrls.map((url, index) => (
                  <div key={index} className="relative aspect-square rounded-lg border group overflow-hidden">
                    <img
                      src={url}
                      alt={`Exame ${index + 1}`}
                      className="absolute inset-0 h-full w-full rounded-lg object-cover transition-transform duration-300 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                      <span className="text-white text-sm font-medium">Exame {index + 1}</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Interpretação */}
              <div className="prose max-w-none rounded-lg border bg-card shadow-sm transition-all duration-300 hover:shadow-md">
                <div className="border-b p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <ClipboardList className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="text-2xl font-semibold m-0">Interpretação</h3>
                  </div>
                  {lastFeedback === 'positive' && (
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-[#6a9d7f]/10 text-[#6a9d7f] rounded-full">
                      <ThumbsUp className="w-4 h-4" />
                      <span className="text-sm font-medium">Análise útil</span>
                    </div>
                  )}
                </div>
                <div 
                  className="p-6 text-card-foreground space-y-3 text-lg"
                  dangerouslySetInnerHTML={{ __html: diagnosis }} 
                />
              </div>

              {/* Quick Feedback Section */}
              <div className="flex flex-col items-center gap-4 p-6 border rounded-lg bg-gradient-to-b from-white to-primary/5 shadow-sm hover:shadow-md transition-all duration-300">
                <div className="text-center space-y-2">
                  <h3 className="text-xl font-semibold text-[#2C3E50]">Sua opinião é muito importante!</h3>
                  <p className="text-base text-muted-foreground">
                    Ajude-nos a melhorar nossa IA avaliando esta análise
                  </p>
                </div>
                
                <div className="flex gap-4">
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={() => handleQuickFeedback('positive')}
                    disabled={lastFeedback !== null}
                    className={cn(
                      "flex items-center gap-2 h-12 px-6 transition-all duration-300 hover:scale-105",
                      lastFeedback === 'positive'
                        ? "border-[#6a9d7f] text-[#6a9d7f] bg-[#6a9d7f]/5"
                        : "hover:bg-[#6a9d7f]/5 hover:border-[#6a9d7f] hover:text-[#6a9d7f]"
                    )}
                  >
                    <ThumbsUp className="w-5 h-5" />
                    <span>Análise Útil</span>
                  </Button>
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={() => handleQuickFeedback('negative')}
                    disabled={lastFeedback !== null}
                    className={cn(
                      "flex items-center gap-2 h-12 px-6 transition-all duration-300 hover:scale-105",
                      lastFeedback === 'negative'
                        ? "border-primary text-primary bg-primary/5"
                        : "hover:bg-primary/5 hover:border-primary hover:text-primary"
                    )}
                  >
                    <ThumbsDown className="w-5 h-5" />
                    <span>Precisa Melhorar</span>
                  </Button>
                </div>

                {!lastFeedback && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground animate-pulse">
                    <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    <span>Sua avaliação nos ajuda a melhorar a precisão da IA</span>
                  </div>
                )}
              </div>

              {/* Showcase após feedback positivo */}
              {lastFeedback === 'positive' && (
                <div className="p-4 sm:p-8 border rounded-lg bg-gradient-to-b from-white to-[#6AB99C]/5 animate-in slide-in-from-bottom-4 duration-500">
                  <div className="space-y-6 sm:space-y-8">
                    {/* Logo Simples com animação de scale */}
                    <div className="flex justify-center">
                      <img 
                        src="/Logo Escrito.png" 
                        alt="Simples.Health" 
                        className="h-12 sm:h-16 w-auto hover:opacity-80 transition-all duration-500 animate-in zoom-in-105"
                      />
                    </div>

                    {/* Estrelas Douradas com animação sequencial */}
                    <div className="flex items-center justify-center gap-1 sm:gap-2">
                      <Star className="w-6 h-6 sm:w-8 sm:h-8 fill-yellow-400 text-yellow-400 animate-in zoom-in duration-300 delay-0" />
                      <Star className="w-6 h-6 sm:w-8 sm:h-8 fill-yellow-400 text-yellow-400 animate-in zoom-in duration-300 delay-150" />
                      <Star className="w-6 h-6 sm:w-8 sm:h-8 fill-yellow-400 text-yellow-400 animate-in zoom-in duration-300 delay-300" />
                      <Star className="w-6 h-6 sm:w-8 sm:h-8 fill-yellow-400 text-yellow-400 animate-in zoom-in duration-300 delay-500" />
                      <Star className="w-6 h-6 sm:w-8 sm:h-8 fill-yellow-400 text-yellow-400 animate-in zoom-in duration-300 delay-700" />
                    </div>
                    
                    <div className="space-y-4 sm:space-y-6 max-w-xl mx-auto animate-in fade-in duration-700 delay-500">
                      <div className="space-y-1 sm:space-y-2 text-center">
                        <h3 className="text-2xl sm:text-3xl font-bold text-black">
                          Mais medicina,
                        </h3>
                        <p className="text-3xl sm:text-4xl font-bold text-[#6AB99C]">
                          menos burocracia
                        </p>
                      </div>

                      <p className="text-base sm:text-xl text-center text-[#2C3E50]/80 leading-relaxed px-4 sm:px-0">
                        Assim como o Simples.ECG revoluciona a análise de eletrocardiogramas, a Simples.Health está transformando a documentação médica com IA avançada.
                      </p>
                      
                      {/* Cards com Gradiente e animação */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mt-4 sm:mt-8 animate-in slide-in-from-bottom-8 duration-700 delay-700 px-4 sm:px-0">
                        <div className="flex flex-col items-center text-center p-4 sm:p-6 rounded-xl bg-gradient-to-b from-[#6AB99C]/5 to-transparent border border-[#6AB99C]/10 hover:border-[#6AB99C]/30 transition-all duration-300 hover:scale-105">
                          <h4 className="text-xl sm:text-2xl font-bold text-[#6AB99C] mb-2">80% menos tempo gasto</h4>
                          <p className="text-xs sm:text-sm text-center text-[#2C3E50]/70">com burocracia e muito mais tempo para o que realmente importa</p>
                        </div>
                        <div className="flex flex-col items-center p-4 sm:p-6 rounded-xl bg-gradient-to-b from-[#6AB99C]/5 to-transparent border border-[#6AB99C]/10 hover:border-[#6AB99C]/30 transition-all duration-300 hover:scale-105">
                          <h4 className="text-xl sm:text-2xl font-bold text-[#6AB99C] mb-2">100% Automático</h4>
                          <p className="text-xs sm:text-sm text-center text-[#2C3E50]/70">geração Receitas, Laudos, Encaminhamentos, Pedidos de Exame e muito mais com um clique</p>
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-center mt-4 sm:mt-8 animate-in slide-in-from-bottom-4 duration-700 delay-1000 px-4 sm:px-0">
                      <Button
                        asChild
                        className="w-full sm:w-auto h-12 sm:h-14 px-6 sm:px-8 text-base sm:text-lg font-medium bg-[#6AB99C] hover:bg-[#6AB99C]/90 text-white transition-all duration-300 rounded-xl shadow-lg hover:shadow-xl hover:scale-[1.02] group"
                      >
                        <a href="https://simples.health" target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2">
                          <span className="text-sm sm:text-base">Conhecer a Plataforma Completa</span>
                          <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-white/10 flex items-center justify-center transition-transform group-hover:translate-x-1">
                            →
                          </div>
                        </a>
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Botões de Ação */}
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 px-4 sm:px-0">
                <Button 
                  size="lg"
                  onClick={handleNewAnalysis} 
                  className="w-full h-12 sm:h-14 text-base sm:text-lg bg-primary hover:bg-primary/90 transition-all duration-300"
                >
                  Nova Análise
                </Button>
                {lastFeedback === 'positive' && (
                  <Button
                    asChild
                    variant="outline"
                    size="lg"
                    className="w-full h-12 sm:h-14 text-base sm:text-lg border-[#6a9d7f] text-[#6a9d7f] hover:bg-[#6a9d7f]/5 transition-all duration-300"
                  >
                    <a href="https://simples.health" target="_blank" rel="noreferrer">
                      Conhecer Simples.Health →
                    </a>
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </main>

      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deseja continuar sem contexto clínico?</AlertDialogTitle>
            <AlertDialogDescription>
              As interpretações são mais precisas quando você fornece o contexto clínico do paciente. 
              Recomendamos adicionar informações como sintomas, histórico médico relevante e outras observações importantes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Voltar e adicionar contexto</AlertDialogCancel>
            <AlertDialogAction onClick={processImages}>
              Continuar sem contexto
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Editor de imagem temporariamente desabilitado */}
      {/* {showCropModal && tempImageUrl && (
        <ImageCropModal
          imageUrl={tempImageUrl}
          isOpen={showCropModal}
          onClose={() => {
            setShowCropModal(false)
            if (tempImageUrl) {
              URL.revokeObjectURL(tempImageUrl)
              setTempImageUrl(null)
            }
          }}
          onSave={handleCroppedImage}
        />
      )} */}

      {/* Barra inferior fixa */}
      {showFooter && (
        <footer className="fixed bottom-0 left-0 right-0 border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <div className="container mx-auto p-4">
            <div className="flex flex-col gap-2">
              <div className="flex items-start gap-2 text-sm text-muted-foreground">
                <AlertTriangle className="h-5 w-5 flex-shrink-0 text-yellow-500" />
                <p>
                  Este aplicativo utiliza inteligência artificial para interpretar imagens, algumas de suas interpretações podem estar incorretas. 
                  Não é um diagnóstico médico e não substitui a necessidade de consultar um profissional. 
                  Todas as interpretações devem ser verificadas por um médico especialista.
                </p>
                <button 
                  onClick={() => setShowFooter(false)}
                  className="sm:hidden ml-2 p-1 rounded-full hover:bg-gray-200 transition-colors"
                  aria-label="Fechar aviso"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="text-center text-sm text-muted-foreground">
                Desenvolvido por{" "}
                <a
                  href="https://simples.health"
                  target="_blank"
                  rel="noreferrer"
                  className="font-medium underline underline-offset-4"
                >
                  Simples.Health
                </a>
              </div>
            </div>
          </div>
        </footer>
      )}

      <Dialog open={showLoadingModal} onOpenChange={setShowLoadingModal}>
        <DialogContent className="sm:max-w-md w-[95%] max-w-[95%] rounded-2xl p-0">
          <div className="flex flex-col items-center justify-center p-4 sm:p-6 space-y-4 sm:space-y-6">
            {/* Loading Animation Container */}
            <div className="relative w-16 sm:w-20 h-16 sm:h-20">
              {/* Outer spinning circle */}
              <div className="absolute inset-0 border-3 sm:border-4 border-primary/20 rounded-full" />
              <div className="absolute inset-0 border-3 sm:border-4 border-primary border-t-transparent rounded-full animate-spin" />
              
              {/* Inner pulsing icon */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="relative">
                  <RefreshCw className="w-6 h-6 sm:w-8 sm:h-8 text-primary animate-spin" />
                  <div className="absolute -top-1 -right-1 w-2 h-2 sm:w-3 sm:h-3 bg-primary rounded-full animate-ping" />
                </div>
              </div>
            </div>

            {/* Loading Text */}
            <div className="space-y-1 sm:space-y-2 text-center">
              <h3 className="text-base sm:text-lg font-semibold text-foreground">
                Processando suas imagens...
              </h3>
              <p className="text-xs sm:text-sm text-muted-foreground">
                Isso pode levar alguns segundos
              </p>
            </div>

            {/* Feedback Message */}
            <div className="flex items-center gap-2 p-3 sm:p-4 rounded-lg bg-primary/5 border border-primary/10 animate-pulse">
              <div className="flex-shrink-0">
                <Star className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-500 fill-yellow-500" />
              </div>
              <p className="text-xs sm:text-sm text-muted-foreground">
                Sua avaliação ao final nos ajuda a melhorar a precisão da IA. Não deixe de avaliar!
              </p>
            </div>

            {/* Progress Dots */}
            <div className="flex gap-1.5">
              <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-primary/30 animate-bounce" style={{ animationDelay: '0ms' }} />
              <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-primary/30 animate-bounce" style={{ animationDelay: '150ms' }} />
              <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-primary/30 animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Menu Mobile */}
      <Sheet>
        <SheetTrigger asChild>
          <Button variant="ghost" size="icon" className="md:hidden">
            <Menu className="h-6 w-6" />
          </Button>
        </SheetTrigger>
        <SheetContent side="right" className="w-72">
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-2">
              <Avatar className="h-8 w-8">
                <AvatarImage src={user?.photoURL || undefined} />
                <AvatarFallback>{user?.displayName?.[0]}</AvatarFallback>
              </Avatar>
              <div className="flex flex-col">
                <span className="text-sm font-medium">{user?.displayName}</span>
                <span className="text-xs text-muted-foreground">{user?.email}</span>
              </div>
            </div>
            <Separator />
            <nav className="flex flex-col gap-2">
              <Link
                to="/faq"
                className="flex items-center gap-2 px-2 py-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <HelpCircle className="h-4 w-4" />
                Dúvidas Frequentes
              </Link>
              <Link
                to="/privacy"
                className="flex items-center gap-2 px-2 py-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <Shield className="h-4 w-4" />
                Política de Privacidade
              </Link>
              <Link
                to="/terms"
                className="flex items-center gap-2 px-2 py-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <FileText className="h-4 w-4" />
                Termos de Uso
              </Link>
              <Separator />
              <button
                onClick={handleSignOut}
                className="flex items-center gap-2 px-2 py-1 text-sm text-red-500 hover:text-red-600 transition-colors"
              >
                <LogOut className="h-4 w-4" />
                Sair
              </button>
            </nav>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  )
} 